import { useTranslation } from 'react-i18next';
import { ModalComponentWrapper } from '@/shared/components';
import { useModalStore } from '@/shared/lib';
import { useSessionStore } from '@/entities/session';
import {
  MfaConfirmationScreen,
  MfaIdentitiesList,
} from '@/entities/mfa-identity';
import { sendTwoFactorIdentityCode } from '@/features/user-settings-2fa';
import {
  useChangePasswordFlow,
  useMfaFlow,
  useChangePasswordForm,
} from '../model';
import { ChangePasswordScreen } from './ChangePasswordScreen';

interface Props {
  onBack: () => void;
}

const ChangePasswordModal = ({ onBack }: Props) => {
  const { t } = useTranslation();
  const { logout } = useSessionStore();

  const { closeDialog } = useModalStore();

  const { form, unmetPasswordChecksBeforeSubmit, passwordChecks, resetCode } =
    useChangePasswordForm();

  const {
    view,
    selectedIdentity,
    identityList,
    modalDescriptionText,
    setMaskedContact,
    startMfaFlow,
    selectIdentity,
  } = useMfaFlow(form);
  const changeFlow = useChangePasswordFlow({
    onMfaRequired: startMfaFlow,
    onLogout: logout,
    onSuccess: () => closeDialog(),
    resetCode,
  });

  return (
    <ModalComponentWrapper
      title={t('personalAccount.userProfile.changePassword.title')}
      description={modalDescriptionText}
      onBack={onBack}>
      {view === 'list' && !identityList && (
        <ChangePasswordScreen
          form={form}
          onSubmit={changeFlow.handleChangePassword}
          isLoading={changeFlow.isLoading}
          error={changeFlow.error || undefined}
          unmetPasswordChecksBeforeSubmit={unmetPasswordChecksBeforeSubmit}
          passwordChecks={passwordChecks}
        />
      )}

      {view === 'screen' && selectedIdentity && (
        <MfaConfirmationScreen
          form={form}
          identityType={selectedIdentity.name}
          mfaCodeFieldName="twoFA.code"
          sendOtpCodeMutationFn={sendTwoFactorIdentityCode}
          setMaskedContact={setMaskedContact}
          onSubmit={() => changeFlow.handleChangePassword(form.getValues())}
          isSubmitLoading={changeFlow.isLoading}
          submitError={changeFlow.error || undefined}
        />
      )}

      {view === 'list' && identityList && (
        <MfaIdentitiesList
          availableIdentities={identityList}
          onSelectIdentity={selectIdentity}
        />
      )}
    </ModalComponentWrapper>
  );
};

export { ChangePasswordModal };
